import "../styles/ErrorBoundary.scss";
import React, { Component } from "react";
import { isRouteErrorResponse, useNavigate, useRouteError } from "react-router-dom";

export function ErrorFallbackUI({
    title = "Something went wrong",
    message = "An unexpected error occurred. Our team has been notified.",
    stack,
    onRetry,
    onGoHome,
}) {
    const [showDetails, setShowDetails] = React.useState(false);
    const hasTwoActions = onRetry && onGoHome;

    return (
        <div className="error-boundary">
            <div className="error-boundary__grid" />
            <div className="error-boundary__glow" />
            <div className="error-boundary__content">
                <div className="error-boundary__status">
                    <span className="error-boundary__status-dot" />
                    Runtime Exception
                </div>

                <div className="error-boundary__code">500</div>
                <h1 className="error-boundary__title">{title}</h1>
                <p className="error-boundary__message">{message}</p>

                {(onRetry || onGoHome) && (
                    <div className={`error-boundary__actions${hasTwoActions ? "" : " error-boundary__actions--single"}`}>
                        {onRetry && (
                            <button type="button" onClick={onRetry} className="error-boundary__button error-boundary__button--primary">
                                Try again
                            </button>
                        )}
                        {onGoHome && (
                            <button type="button" onClick={onGoHome} className="error-boundary__button error-boundary__button--secondary">
                                Go home
                            </button>
                        )}
                    </div>
                )}

                {stack && (
                    <div className="error-boundary__details">
                        <button
                            type="button"
                            onClick={() => setShowDetails((previous) => !previous)}
                            className="error-boundary__details-toggle"
                            aria-expanded={showDetails}
                        >
                            <span className={`error-boundary__details-chevron${showDetails ? " error-boundary__details-chevron--open" : ""}`} aria-hidden="true">
                                &gt;
                            </span>
                            {showDetails ? "Hide" : "Show"} stack trace
                        </button>

                        {showDetails && <pre className="error-boundary__stack">{stack}</pre>}
                    </div>
                )}

                <div className="error-boundary__footer">
                    <span>Error boundary caught</span>
                    <span>{new Date().toISOString()}</span>
                </div>
            </div>
        </div>
    );
}

export class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null };
        this.handleRetry = this.handleRetry.bind(this);
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        this.setState({ errorInfo });
        console.error("[ErrorBoundary]", error, errorInfo);
    }

    handleRetry() {
        this.setState({ hasError: false, error: null, errorInfo: null });
    }

    render() {
        const { hasError, error, errorInfo } = this.state;
        const { children, fallback } = this.props;

        if (hasError) {
            if (fallback) return fallback;

            return (
                <ErrorFallbackUI
                    title="Something went wrong"
                    message={error?.message || "An unexpected error occurred."}
                    stack={errorInfo?.componentStack || error?.stack}
                    onRetry={this.handleRetry}
                    onGoHome={() => {
                        window.location.href = "/";
                    }}
                />
            );
        }

        return children;
    }
}

export function RouterErrorBoundary() {
    const error = useRouteError();
    const navigate = useNavigate();

    let title = "Page not found";
    let message = "The page you are looking for does not exist or has been moved.";
    let errorCode = "404";

    if (isRouteErrorResponse(error)) {
        errorCode = String(error.status);
        title = error.status === 404 ? "Page not found" : error.status === 403 ? "Access denied" : "Something went wrong";
        message = error.statusText || message;
    } else if (error instanceof Error) {
        errorCode = "500";
        title = "Runtime error";
        message = error.message;
    }

    return (
        <div className="error-boundary">
            <div className="error-boundary__grid" />
            <div className="error-boundary__glow" />
            <div className="error-boundary__content">
                <div className="error-boundary__status">
                    <span className="error-boundary__status-dot" />
                    Router Error
                </div>

                <div className="error-boundary__code">{errorCode}</div>
                <h1 className="error-boundary__title">{title}</h1>
                <p className="error-boundary__message">{message}</p>

                <div className="error-boundary__actions">
                    <button type="button" onClick={() => navigate(-1)} className="error-boundary__button error-boundary__button--secondary">
                        Go back
                    </button>
                    <button type="button" onClick={() => navigate("/")} className="error-boundary__button error-boundary__button--primary">
                        Go home
                    </button>
                </div>

                <div className="error-boundary__footer">
                    <span>React Router DOM</span>
                    <span>{new Date().toISOString()}</span>
                </div>
            </div>
        </div>
    );
}

export default ErrorBoundary;